import { useState, useContext } from "react";
import { Form, Button, FloatingLabel, Spinner } from "react-bootstrap";
import { Navigate, Link } from "react-router-dom";
import { Notyf } from "notyf";
import UserContext from "../context/UserContext";

export default function Login() {
  const notyf = new Notyf();
  const { user, setUser } = useContext(UserContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const isActive = email.trim() !== "" && password.trim() !== "";

  const authenticate = async (e) => {
    e.preventDefault();

    if (!isActive || loading) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.trim(),
            password,
          }),
        },
      );

      const data = await response.json();

      if (data.access) {
        localStorage.setItem("token", data.access);

        await retrieveUserDetails(data.access);

        setEmail("");
        setPassword("");

        notyf.success("Successful Login");
      } else {
        if (data.message === "Incorrect email or password") {
          notyf.error("Incorrect credentials. Try again.");
        } else {
          notyf.error("Login failed. Please check your credentials.");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      notyf.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const retrieveUserDetails = async (token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.user && data.user._id) {
        setUser({
          id: data.user._id,
          isAdmin: data.user.isAdmin,
        });
      } else {
        throw new Error("User details could not be retrieved.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);

      localStorage.removeItem("token");

      notyf.error("Failed to fetch user details.");
    }
  };

  if (user.id !== null) {
    return <Navigate to="/" />;
  }

  return (
    <section className="auth-section">
      <div className="container">
        <div className="form-container login-container">
          {/* Heading */}
          <div className="auth-heading text-center">
            <span className="section-eyebrow">WELCOME BACK</span>

            <h1>Login to your account</h1>

            <p>Sign in to continue enjoying your favorite desserts.</p>
          </div>

          <Form onSubmit={authenticate}>
            {/* Email */}
            <FloatingLabel
              controlId="login-email"
              label="Email address"
              className="mb-3"
            >
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </FloatingLabel>

            {/* Password */}
            <FloatingLabel
              controlId="login-password"
              label="Password"
              className="mb-3"
            >
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
            </FloatingLabel>

            {/* Login button */}
            <Button
              type="submit"
              className="dh-button auth-submit-button"
              disabled={!isActive || loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>

            {/* Register */}
            <div className="auth-footer text-center">
              <p>
                Don't have an account yet?{" "}
                <Link to="/register">Create one</Link>
              </p>
            </div>
          </Form>
        </div>
      </div>
    </section>
  );
}
